'use client';

import { c as _c } from "react/compiler-runtime";
import { parsePhoneNumber } from 'libphonenumber-js';
import { useRef, useState } from 'react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/app-command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/app-popover';
import type { FieldShellSize } from '@/components/ui/field-shell-size';
import { Icon } from '@/components/ui/icon';
import { ArrowDown01Icon, Tick02Icon } from '@/components/ui/icon/glyphs';
import { SilkSheetExternalOverlay } from '@/components/ui/silk-sheet';
import { COUNTRIES, type CountryData, findCountryByDialCode, findCountryByIso2 } from '@/lib/phone/countries';
import { cn } from '@/lib/ui/utils';
const PHONE_ROW_HEIGHT: Record<FieldShellSize, string> = {
  sm: 'h-8',
  default: 'h-9',
  lg: 'h-10',
  xl: 'h-12'
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PhoneInputProps {
  /** E.164 value (e.g. "+447983434123") or empty string */
  value: string;
  /** Called with the full E.164 string on every change */
  onChange: (value: string) => void;
  /** ISO 3166-1 alpha-2 default country (e.g. "GB") */
  defaultCountry?: string;
  /** Placeholder for the number portion */
  placeholder?: string;
  /** Label for screen readers on the country picker */
  countryLabel?: string;
  /** Search placeholder inside the country picker */
  searchPlaceholder?: string;
  /** Empty search results label */
  noResultsLabel?: string;
  disabled?: boolean;
  /** Whether the current value is invalid (shows red border) */
  invalid?: boolean;
  className?: string;
  /** Row height — matches `FieldShell` / `InputField` sizes. Defaults to `default` (36px). */
  size?: FieldShellSize | undefined;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const US_COUNTRY: CountryData | undefined = findCountryByIso2('US');
const FALLBACK_COUNTRY: CountryData = COUNTRIES[0] as CountryData;
const DEFAULT_COUNTRY: CountryData = US_COUNTRY ?? FALLBACK_COUNTRY;
function resolveDefaultCountry(iso2: string): CountryData {
  return findCountryByIso2(iso2) ?? DEFAULT_COUNTRY;
}

/**
 * Parse an E.164 string into country + national number.
 */
function parseE164(value: string, fallback: CountryData): {
  country: CountryData;
  nationalNumber: string;
} {
  if (!value || value.length < 2) {
    return {
      country: fallback,
      nationalNumber: ''
    };
  }
  try {
    const parsed = parsePhoneNumber(value);
    if (parsed?.country) {
      const countryData = findCountryByIso2(parsed.country);
      if (countryData) {
        return {
          country: countryData,
          nationalNumber: parsed.nationalNumber
        };
      }
    }
  } catch {
    // SILENT-CATCH: invalid phone string — fall through to dial code matching
  }
  const matched = findCountryByDialCode(value);
  if (matched) {
    const nationalPart = value.slice(matched.dialCode.length);
    return {
      country: matched,
      nationalNumber: nationalPart
    };
  }
  return {
    country: fallback,
    nationalNumber: value.replace(/^\+/, '')
  };
}

/**
 * Initialize country + national number from the initial value prop.
 */
function initFromValue(value: string, defaultCountryIso2: string): {
  country: CountryData;
  national: string;
} {
  const fallback = resolveDefaultCountry(defaultCountryIso2);
  if (!value) return {
    country: fallback,
    national: ''
  };
  const parsed = parseE164(value, fallback);
  return {
    country: parsed.country,
    national: parsed.nationalNumber
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PhoneInput(t0) {
  const $ = _c(69);
  const {
    value,
    onChange,
    defaultCountry: t1,
    placeholder: t2,
    countryLabel: t3,
    searchPlaceholder: t4,
    noResultsLabel: t5,
    disabled: t6,
    invalid: t7,
    className,
    size: t8
  } = t0;
  const defaultCountry = t1 === undefined ? "US" : t1;
  const placeholder = t2 === undefined ? "Phone number" : t2;
  const countryLabel = t3 === undefined ? "Select country" : t3;
  const searchPlaceholder = t4 === undefined ? "Search countries..." : t4;
  const noResultsLabel = t5 === undefined ? "No country found." : t5;
  const disabled = t6 === undefined ? false : t6;
  const invalid = t7 === undefined ? false : t7;
  const size = t8 === undefined ? "default" : t8;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const countryPopoverRef = useRef(null);
  const inputRef = useRef(null);
  let t9;
  if ($[0] !== defaultCountry || $[1] !== value) {
    t9 = () => initFromValue(value, defaultCountry);
    $[0] = defaultCountry;
    $[1] = value;
    $[2] = t9;
  } else {
    t9 = $[2];
  }
  const [initDone] = useState(t9);
  const [emptyInputCountry, setEmptyInputCountry] = useState(initDone.country);
  let t10;
  if ($[3] !== emptyInputCountry || $[4] !== value) {
    t10 = parseE164(value, emptyInputCountry);
    $[3] = emptyInputCountry;
    $[4] = value;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  const parsedValue = t10;
  const country = value.length > 0 ? parsedValue.country : emptyInputCountry;
  const nationalNumber = parsedValue.nationalNumber;
  let t11;
  if ($[6] !== onChange) {
    t11 = function emitValue(newValue) {
      onChange(newValue);
    };
    $[6] = onChange;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  const emitValue = t11;
  const handleCountrySelect = function handleCountrySelect(selected) {
    setPopoverOpen(false);
    setEmptyInputCountry(selected);
    if (nationalNumber.length > 0) {
      emitValue(`${selected.dialCode}${nationalNumber}`);
    }
    setTimeout(() => inputRef.current?.focus(), 0);
  };
  let t12;
  if ($[8] !== country.dialCode || $[9] !== emitValue) {
    t12 = function handleInputChange(inputValue) {
      if (inputValue.startsWith("+")) {
        const cleaned = inputValue.replace(/[\s\-()]/g, "");
        const matched = findCountryByDialCode(cleaned);
        if (matched && cleaned.length > matched.dialCode.length) {
          const national = cleaned.slice(matched.dialCode.length);
          setEmptyInputCountry(matched);
          emitValue(`${matched.dialCode}${national}`);
          return;
        }
        if (matched) {
          setEmptyInputCountry(matched);
          emitValue(cleaned);
        }
        return;
      }
      const digitsOnly = inputValue.replace(/[^\d]/g, "");
      if (digitsOnly.length > 0) {
        emitValue(`${country.dialCode}${digitsOnly}`);
      } else {
        emitValue("");
      }
    };
    $[8] = country.dialCode;
    $[9] = emitValue;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  const handleInputChange = t12;
  const t13 = PHONE_ROW_HEIGHT[size];
  const t14 = invalid && "border-feedback-error-border hover:border-feedback-error-border focus-within:border-feedback-error-border focus-within:ring-feedback-error-border/30 dark:focus-within:ring-feedback-error-border/40";
  const t15 = disabled && "pointer-events-none opacity-50";
  let t16;
  if ($[11] !== className || $[12] !== t13 || $[13] !== t14 || $[14] !== t15) {
    t16 = cn("flex w-full items-center rounded-control border border-solid border-border-primary bg-background-primary shadow-none", t13, "transition-[color,background-color,border-color,box-shadow]", "hover:border-border-brand focus-within:border-border-brand", "focus-within:ring-[3px] focus-within:ring-border-primary/50", t14, t15, className);
    $[11] = className;
    $[12] = t13;
    $[13] = t14;
    $[14] = t15;
    $[15] = t16;
  } else {
    t16 = $[15];
  }
  const T0 = Popover;
  const t17 = true;
  let t18;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = cn("flex h-full shrink-0 items-center gap-1.5 border-r border-border-primary bg-background-tertiary/40 px-3 py-2", "text-body text-foreground-primary transition-colors", "hover:bg-background-tertiary/50 focus-visible:outline-none", "rounded-l-[calc(var(--radius-control)-1px)]");
    $[16] = t18;
  } else {
    t18 = $[16];
  }
  let t19;
  if ($[17] !== country.emoji) {
    t19 = <span className="text-lg leading-none">{country.emoji}</span>;
    $[17] = country.emoji;
    $[18] = t19;
  } else {
    t19 = $[18];
  }
  let t20;
  if ($[19] !== country.dialCode) {
    t20 = <span className="text-body font-medium tabular-nums">{country.dialCode}</span>;
    $[19] = country.dialCode;
    $[20] = t20;
  } else {
    t20 = $[20];
  }
  let t21;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Icon icon={ArrowDown01Icon} className="size-3 text-foreground-secondary" />;
    $[21] = t21;
  } else {
    t21 = $[21];
  }
  let t22;
  if ($[22] !== countryLabel || $[23] !== disabled || $[24] !== t19 || $[25] !== t20) {
    t22 = <PopoverTrigger asChild={true}><button type="button" disabled={disabled} aria-label={countryLabel} className={t18}>{t19}{t20}{t21}</button></PopoverTrigger>;
    $[22] = countryLabel;
    $[23] = disabled;
    $[24] = t19;
    $[25] = t20;
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  const T1 = PopoverContent;
  const t23 = "w-75 p-0";
  const t24 = "start";
  const t25 = _temp;
  const T2 = Command;
  const t26 = _temp3;
  let t27;
  if ($[27] !== searchPlaceholder) {
    t27 = <CommandInput placeholder={searchPlaceholder} />;
    $[27] = searchPlaceholder;
    $[28] = t27;
  } else {
    t27 = $[28];
  }
  const T3 = CommandList;
  const t28 = "max-h-70";
  let t29;
  if ($[29] !== noResultsLabel) {
    t29 = <CommandEmpty>{noResultsLabel}</CommandEmpty>;
    $[29] = noResultsLabel;
    $[30] = t29;
  } else {
    t29 = $[30];
  }
  const T4 = CommandGroup;
  const t30 = COUNTRIES.map(c => <CommandItem key={c.iso2} value={`${c.name} ${c.dialCode} ${c.iso2}`} onSelect={() => handleCountrySelect(c)} className="flex items-center gap-2.5"><span className="text-lg leading-none">{c.emoji}</span><span className="flex-1 truncate text-body">{c.name}</span><span className="text-caption tabular-nums text-foreground-secondary">{c.dialCode}</span>{c.iso2 === country.iso2 && <Icon icon={Tick02Icon} className="size-4 text-foreground-primary" />}</CommandItem>);
  let t31;
  if ($[31] !== T4 || $[32] !== t30) {
    t31 = <T4>{t30}</T4>;
    $[31] = T4;
    $[32] = t30;
    $[33] = t31;
  } else {
    t31 = $[33];
  }
  let t32;
  if ($[34] !== T3 || $[35] !== t29 || $[36] !== t31) {
    t32 = <T3 className={t28}>{t29}{t31}</T3>;
    $[34] = T3;
    $[35] = t29;
    $[36] = t31;
    $[37] = t32;
  } else {
    t32 = $[37];
  }
  let t33;
  if ($[38] !== T2 || $[39] !== t26 || $[40] !== t27 || $[41] !== t32) {
    t33 = <T2 filter={t26}>{t27}{t32}</T2>;
    $[38] = T2;
    $[39] = t26;
    $[40] = t27;
    $[41] = t32;
    $[42] = t33;
  } else {
    t33 = $[42];
  }
  let t34;
  if ($[43] !== T1 || $[44] !== t25 || $[45] !== t33) {
    t34 = <T1 ref={countryPopoverRef} className={t23} align={t24} onEscapeKeyDown={t25}>{t33}</T1>;
    $[43] = T1;
    $[44] = t25;
    $[45] = t33;
    $[46] = t34;
  } else {
    t34 = $[46];
  }
  let t35;
  if ($[47] !== popoverOpen) {
    t35 = popoverOpen ? <SilkSheetExternalOverlay contentGetter={() => countryPopoverRef.current?.parentElement} selfManagedInertOutside={true} /> : null;
    $[47] = popoverOpen;
    $[48] = t35;
  } else {
    t35 = $[48];
  }
  let t36;
  if ($[49] !== T0 || $[50] !== popoverOpen || $[51] !== t22 || $[52] !== t34 || $[53] !== t35) {
    t36 = <T0 modal={t17} open={popoverOpen} onOpenChange={setPopoverOpen}>{t22}{t34}{t35}</T0>;
    $[49] = T0;
    $[50] = popoverOpen;
    $[51] = t22;
    $[52] = t34;
    $[53] = t35;
    $[54] = t36;
  } else {
    t36 = $[54];
  }
  let t37;
  if ($[55] !== handleInputChange) {
    t37 = e => handleInputChange(e.target.value);
    $[55] = handleInputChange;
    $[56] = t37;
  } else {
    t37 = $[56];
  }
  const t38 = invalid || undefined;
  let t39;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = cn("flex-1 bg-transparent px-3 py-2 text-body text-foreground-primary", "placeholder:text-foreground-tertiary", "focus-visible:outline-none", "rounded-r-[calc(var(--radius-control)-1px)]");
    $[57] = t39;
  } else {
    t39 = $[57];
  }
  let t40;
  if ($[58] !== disabled || $[59] !== inputRef || $[60] !== nationalNumber || $[61] !== placeholder || $[62] !== t37 || $[63] !== t38) {
    t40 = <input ref={inputRef} type="tel" inputMode="tel" value={nationalNumber} onChange={t37} placeholder={placeholder} aria-label={placeholder} disabled={disabled} aria-invalid={t38} className={t39} />;
    $[58] = disabled;
    $[59] = inputRef;
    $[60] = nationalNumber;
    $[61] = placeholder;
    $[62] = t37;
    $[63] = t38;
    $[64] = t40;
  } else {
    t40 = $[64];
  }
  let t41;
  if ($[65] !== t16 || $[66] !== t36 || $[67] !== t40) {
    t41 = <div className={t16}>{t36}{t40}</div>;
    $[65] = t16;
    $[66] = t36;
    $[67] = t40;
    $[68] = t41;
  } else {
    t41 = $[68];
  }
  return t41;
}
function _temp3(value_0, search) {
  const v = value_0.toLowerCase();
  const s = search.toLowerCase().trim();
  if (s.length === 0) {
    return 1;
  }
  const dialCodePart = v.split(" ").find(_temp2);
  if (dialCodePart === s) {
    return 2;
  }
  if (v.includes(s)) {
    return 1;
  }
  return 0;
}
function _temp2(p) {
  return p.startsWith("+");
}
function _temp(event) {
  return event.stopPropagation();
}